import { useMemo, useState } from "react";
import { AdminPageShell } from "@/components/shared/admin-page-shell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CreateBackupDialog } from "@/features/backups/components/CreateBackupDialog";
import { ScheduleCard } from "@/features/backups/components/ScheduleCard";
import { BackupsTable } from "@/features/backups/components/BackupsTable";
import { useBackups } from "@/features/backups/hooks/useBackupsQueries";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import type { Backup } from "@/types/backup";

function filterByDateRange(
  backups: Backup[],
  from?: string,
  to?: string,
): Backup[] {
  if (!from && !to) return backups;
  const fromMs = from ? new Date(`${from}T00:00:00`).getTime() : 0;
  const toMs = to ? new Date(`${to}T23:59:59.999`).getTime() : Number.POSITIVE_INFINITY;
  return backups.filter((b) => {
    const t = new Date(b.createdAt).getTime();
    return t >= fromMs && t <= toMs;
  });
}

export default function BackupsPage() {
  const { canManageBackups } = useUserPermissions();
  const { data, isLoading, error } = useBackups();
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const filtered = useMemo(
    () => filterByDateRange(data?.items ?? [], fromDate || undefined, toDate || undefined),
    [data?.items, fromDate, toDate],
  );

  return (
    <AdminPageShell
      title="النسخ الاحتياطي"
      description="إنشاء واستعادة نسخ احتياطية للنظام"
      actions={canManageBackups ? <CreateBackupDialog /> : undefined}
      isLoading={isLoading}
      error={error}
    >
      <div className="space-y-6">
        <ScheduleCard />
        <div className="rounded-lg border border-border bg-card p-4 space-y-4">
          <h3 className="font-semibold text-right">تصفية حسب التاريخ</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 text-right">
              <Label htmlFor="backup-from">من تاريخ</Label>
              <Input
                id="backup-from"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div className="space-y-2 text-right">
              <Label htmlFor="backup-to">إلى تاريخ</Label>
              <Input
                id="backup-to"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
          </div>
          {(fromDate || toDate) && (
            <p className="text-sm text-muted-foreground text-right">
              عرض {filtered.length} من {(data?.items ?? []).length} نسخة
            </p>
          )}
        </div>
        <BackupsTable backups={filtered} canRestore={canManageBackups} />
      </div>
    </AdminPageShell>
  );
}
